import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Avatar, Badge, Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SummarizeIcon from '@mui/icons-material/Summarize';
import TuneIcon from '@mui/icons-material/Tune';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import appIcon from './appicon.png';
import { useAppearance } from '../common/theme/ThemeMode';

import { sessionActions } from '../store';
import { useRestriction } from '../common/util/permissions';
import useZenyIssueCount from '../attention/useZenyIssueCount';
import useUnreadAlerts from '../attention/unread';
import { useOperations } from '../operations/OperationsProvider';
import useOperationalPermissions from '../common/util/operationalPermissions';
import { matchesPath, sectionActive } from './navigation';
import brand from './brand';
import Wordmark from './Wordmark';

// Lifted from the brand teal so it holds contrast against the navy rail; the palette
// value itself is too dark to read as an accent on this surface.
const ACCENT = '#3fc8bd';

const useStyles = makeStyles((theme) => ({
  rail: {
    position: 'fixed',
    // Inset from all four edges rather than butting against them, so the navigation reads as a
    // panel laid on the page rather than a wall built into the window.
    top: theme.dimensions.navRailInset,
    left: theme.dimensions.navRailInset,
    bottom: theme.dimensions.navRailInset,
    zIndex: 8,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#102c3e',
    borderRadius: 22,
    // What separates the panel from the page. The shadow carries it in daylight; against the dark
    // mode's near-black ground a shadow is invisible, so the hairline does the work there.
    boxShadow: '0 10px 30px rgba(8, 20, 30, 0.26), 0 2px 8px rgba(8, 20, 30, 0.18)',
    border: '1px solid rgba(255,255,255,0.07)',
    color: 'rgba(255,255,255,0.72)',
    transition: 'width .18s ease',
    // Keeps the header hairline and the hover tints inside the rounded corners.
    overflow: 'hidden',
  },
  railExpanded: { width: theme.dimensions.navRailWidth },
  railCollapsed: { width: theme.dimensions.navRailWidthCollapsed },
  brandCollapsed: { padding: theme.spacing(1.5, 1), gap: theme.spacing(0.75), justifyContent: 'center', alignItems: 'center' },
  // A customer's logo is as often a wordmark - "TATA STEEL" - as a square badge. A wordmark squeezed
  // into a 32px box is unreadable, so a wide one is given the header's full width and the name sits
  // under it instead of beside it. The toggle keeps its slot either way.
  brandStack: { display: 'flex', flexDirection: 'column', gap: theme.spacing(1), minWidth: 0, flex: 1 },
  logoSquare: { width: 32, height: 32, objectFit: 'contain', flexShrink: 0 },
  logoWide: { maxWidth: '100%', maxHeight: 40, width: 'auto', height: 'auto', objectFit: 'contain', alignSelf: 'flex-start' },
  brand: {
    // Grows with the name rather than clipping it: a long plant name is the customer's own, and
    // eliding it to "Hindalco Industries Alumini…" is worse than spending another line on it.
    minHeight: 72,
    display: 'flex',
    // Top-aligned, so the mark sits with the first line of the name rather than floating against
    // the middle of a four-line one.
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.5, 2),
    flexShrink: 0,
    // A hairline under the brand separates identity from navigation without
    // spending a whole block of empty space on the distinction.
    borderBottom: '1px solid rgba(255,255,255,0.09)',
  },
  mark: {
    width: 30,
    height: 30,
    borderRadius: 9,
    backgroundColor: theme.palette.secondary.main,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 15,
    flexShrink: 0,
  },
  wordmark: {
    color: '#fff',
    fontWeight: 600,
    letterSpacing: '0.06em',
    fontSize: 15,
    lineHeight: 1.25,
    // Wraps rather than truncates, to at most two lines; a name longer than that is elided at the
    // end of the second, where the reader has already had the distinguishing part of it.
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 4,
    overflow: 'hidden',
    overflowWrap: 'anywhere',
  },
  nav: {
    flex: 1,
    padding: theme.spacing(1.5, 1.25),
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    overflowY: 'auto',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    // 44px keeps the standard minimum touch target while letting all nine sections,
    // the header and the account row fit a 720px-tall screen without the rail
    // scrolling — at 48px it overflowed by a few pixels and grew a scrollbar.
    minHeight: 44,
    padding: theme.spacing(1, 1.5),
    borderRadius: 9,
    cursor: 'pointer',
    color: 'inherit',
    whiteSpace: 'nowrap',
    border: 'none',
    background: 'none',
    font: 'inherit',
    width: '100%',
    textAlign: 'left',
    transition: 'background-color .15s ease, color .15s ease',
    '& > .MuiBadge-root': { flexShrink: 0 },
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)', color: '#fff' },
    '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: -2 },
  },
  // A page inside the open group: indented, quieter, and shorter, so the six groups stay the
  // structure and their contents read as contents rather than as six more destinations.
  child: {
    minHeight: 36,
    paddingLeft: theme.spacing(5.25),
    fontSize: 13,
    opacity: 0.78,
  },
  childActive: {
    opacity: 1,
    color: '#fff',
    backgroundColor: 'rgba(255,255,255,0.07)',
  },
  // The active row reads as a raised surface with a crisp accent rule, rather than a
  // tinted block: at a glance the eye finds the bar, not a wash of colour.
  itemActive: {
    backgroundColor: 'rgba(255,255,255,0.10)',
    color: '#fff',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 9,
      bottom: 9,
      width: 3,
      borderRadius: '0 3px 3px 0',
      backgroundColor: ACCENT,
    },
    '& svg': { color: ACCENT },
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.13)' },
  },
  itemLabel: { fontSize: 13.5, fontWeight: 500, flex: 1, letterSpacing: '0.01em' },
  brandSubtitle: { fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 3, letterSpacing: '0.02em' },
  count: {
    minWidth: 20, height: 18, lineHeight: '18px', padding: '0 6px', borderRadius: 9, background: '#d4564f', color: '#fff', fontSize: 11, fontWeight: 600, textAlign: 'center', flexShrink: 0,
  },
  footer: {
    padding: theme.spacing(1, 1.25, 1.25),
    flexShrink: 0,
    borderTop: '1px solid rgba(255,255,255,0.09)',
  },
  divider: { borderColor: 'rgba(255,255,255,0.14)' },
  account: {
    width: '100%',
    border: 0,
    background: 'none',
    font: 'inherit',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    cursor: 'pointer',
    borderRadius: 9,
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
    '&:focus-visible': { outline: '2px solid #47d7bd' },
  },
  avatar: {
    width: 30,
    height: 30,
    backgroundColor: theme.palette.secondary.main,
    fontSize: 14,
  },
  accountRole: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.55)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  accountName: {
    fontSize: 13,
    fontWeight: 500,
    color: '#fff',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // One control in one place. It used to be a chevron in the header when open and a different
  // chevron down in the footer when shut, so the way back was never where the way in had been.
  collapseButton: {
    color: 'rgba(255,255,255,0.55)',
    flexShrink: 0,
    alignSelf: 'flex-start',
    '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
  },
  collapseButtonExpanded: { marginLeft: 'auto' },
}));

// Naming the signed-in role is worth the line: which sections a person can even reach
// depends on it, so it is genuine context rather than decoration.
const ROLE_LABELS = {
  TENANT_ADMINISTRATOR: 'Tenant administrator',
  PLANT_OPERATOR: 'Plant operator',
  STANDARD_USER: 'Standard user',
  PLATFORM_ADMINISTRATOR: 'Platform administrator',
};

const NavRail = ({ collapsed, onToggle, onNotifications }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const [anchorEl, setAnchorEl] = useState(null);
  // Set from the image itself rather than assumed: a logo wider than it is tall by half again is a
  // wordmark and is laid out as one.
  const [wideLogo, setWideLogo] = useState(false);
  const { appearance, setAppearance } = useAppearance();
  // Collapsed, a group shows only its icon - so the pages inside it need somewhere to go or they
  // become unreachable, which is what happened the moment the rail was grouped.
  const [group, setGroup] = useState(null);

  const user = useSelector((state) => state.session.user);
  const disableReports = useRestriction('disableReports');
  const { read, operate, administer, haulier } = useOperationalPermissions();
  const zenyCount = useZenyIssueCount();
  // Today carries what is outstanding; the bell carries what has happened since this person looked.
  const unread = useUnreadAlerts();
  const { data } = useOperations();

  // Listed only where the person can actually use them. A user given a few vehicles to watch gets
  // tracking, exceptions and reports; the desks and configuration belong to the people who run
  // them. The server enforces all of this regardless - this only stops offering doors that open
  // onto a permission message.
  // Six groups rather than eleven doors. Six of the old entries showed trips on a map or in a list
  // and nobody arriving could tell which to open; Analytics had no entry at all and was reachable
  // only by typing its address. Nothing is removed - every page keeps its route and its permission -
  // they are gathered under the question each of them answers.
  const sections = [
    {
      key: 'today',
      label: 'Today',
      icon: <DashboardIcon fontSize="small" />,
      path: '/',
      badge: zenyCount,
      show: read,
      children: [
        { key: 'overview', label: 'In-plant operations', path: '/', show: read },
        { key: 'outbound-operations', label: 'Outbound operations', path: '/outbound-operations', show: read },
        // The third kind of traffic: lorries running between sites that never meet a gate, so
        // neither of the two above counts them.
        { key: 'area-movement', label: 'Area movement', path: '/area-movement', show: read },
        // The page opens on its own date range, like every other page. Where that leaves out an
        // exception this badge is counting, the page says so and offers to widen it - which is
        // better than arriving at a screen whose date boxes are empty and look unset.
        { key: 'exceptions', label: 'Exceptions', path: '/exceptions', badge: zenyCount, show: read },
      ],
    },
    {
      key: 'desk',
      label: 'Desk',
      icon: <MeetingRoomIcon fontSize="small" />,
      path: '/gate-operations',
      show: operate,
      children: [
        { key: 'gate', label: 'Gate operations', path: '/gate-operations', show: operate },
        { key: 'yard', label: 'Parking intake', path: '/yard-intake', show: operate },
        { key: 'shift', label: 'Shift desk', path: '/shift-desk', show: operate },
      ],
    },
    {
      key: 'tracking',
      label: 'Tracking',
      icon: <MyLocationIcon fontSize="small" />,
      path: '/live',
      show: read,
      children: [
        { key: 'live', label: 'Live tracking', path: '/live', show: read },
        { key: 'plant', label: 'In the plant', path: '/plant-tracking', show: read },
        { key: 'outbound', label: 'Outbound', path: '/outbound-tracking', show: read },
        { key: 'inside-areas', label: 'Area occupancy', path: '/inside-areas', show: read },
        { key: 'between-areas', label: 'Area journeys', path: '/between-areas', show: read },
        { key: 'fleet-insights', label: 'Fleet insights', path: '/fleet-insights', show: read },
        { key: 'vehicle-replay', label: 'Vehicle replay', path: '/vehicle-replay', show: read },
      ],
    },
    ...(disableReports ? [] : [{
      key: 'reporting',
      label: 'Reports',
      icon: <SummarizeIcon fontSize="small" />,
      path: '/reports',
      show: read,
      children: [
        { key: 'reports', label: 'Reports', path: '/reports', show: read },
        { key: 'analytics', label: 'Analytics', path: '/analytics', show: read },
      ],
    }]),
    {
      // Every trip, unfiltered, and a section of its own rather than a line inside Tracking.
      //
      // In the plant and Outbound answer "what is happening now" for one half of the yard each; this
      // answers "find me that load", which is what somebody asks when a customer rings about a
      // delivery last Tuesday. Buried as a child of a collapsed group it was invisible until you
      // thought to open Tracking, which is no use for the one thing people look up by name.
      key: 'trips', label: 'Trips', icon: <ReceiptLongIcon fontSize="small" />, path: '/trips', show: read, children: [],
    },
    {
      key: 'fleet', label: 'Vehicles', icon: <LocalShippingIcon fontSize="small" />, path: '/vehicles', show: operate || haulier, children: [],
    },
    {
      key: 'configuration', label: 'Configuration', icon: <TuneIcon fontSize="small" />, path: '/configuration', show: administer, children: [],
    },
  ].filter((section) => section.show)
    .filter((section) => !haulier || ['tracking', 'trips', 'fleet'].includes(section.key))
    .map((section) => ({
      ...section,
      children: (section.children || [])
        .filter((child) => child.show)
        .filter((child) => !haulier || ['live', 'trips', 'fleet-insights', 'vehicle-replay'].includes(child.key)),
    }));

  // Both answers come from one place, so a group cannot disagree with the pages listed under it -
  // Today claimed not to own Outbound operations, and closed itself the moment you opened it.
  const matches = (path) => matchesPath(pathname, path);
  const isActive = (section) => sectionActive(pathname, section);

  const handleLogout = async () => {
    setAnchorEl(null);
    await fetch('/api/session', { method: 'DELETE' });
    navigate('/login');
    dispatch(sessionActions.updateUser(null));
  };

  return (
    <div className={`${classes.rail} ${collapsed ? classes.railCollapsed : classes.railExpanded}`}>
      <div className={`${classes.brand} ${collapsed ? classes.brandCollapsed : ''}`}>
        {(() => {
          const custom = data.branding?.logoDataUrl;
          const title = data.branding?.displayName || brand.wordmark;
          // A wordmark cannot be read at the width of a collapsed rail - six to one means nine
          // pixels tall - so the badge carries the plant's initials instead of a sliver of its logo.
          const initials = title
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((word) => word[0])
            .join('')
            .toUpperCase();
          if (custom && wideLogo && collapsed) {
            return (
              <div className={classes.mark} title={title} aria-label={title}>{initials}</div>
            );
          }
          const image = custom && (
            <img
              src={custom}
              alt="Organization logo"
              onLoad={(event) => setWideLogo(event.target.naturalHeight > 0
                && event.target.naturalWidth / event.target.naturalHeight > 1.5)}
              className={wideLogo ? classes.logoWide : classes.logoSquare}
            />
          );
          const logo = custom
            ? image
            : <img src={brand.mark || appIcon} alt="" className={classes.logoSquare} style={{ borderRadius: 8 }} />;
          const name = !collapsed && (
            <div style={{ minWidth: 0 }}>
              <div className={classes.wordmark}><Wordmark name={title} /></div>
              <div className={classes.brandSubtitle}>Fleet operations</div>
            </div>
          );
          // A wordmark takes the width and the name goes beneath it; a square mark keeps the name
          // alongside, which is how it has always read.
          if (wideLogo && !collapsed) {
            return (
              <div className={classes.brandStack}>
                {logo}
                {name}
              </div>
            );
          }
          return (
            <>
              {logo}
              {name}
            </>
          );
        })()}
        <IconButton
          size="small"
          className={`${classes.collapseButton} ${collapsed ? '' : classes.collapseButtonExpanded}`}
          onClick={onToggle}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
        </IconButton>
      </div>

      <nav className={classes.nav} aria-label="Main navigation">
        {sections.map((section) => {
          const active = isActive(section);
          const content = (
            <button
              type="button"
              key={section.key}
              className={`${classes.item} ${active ? classes.itemActive : ''}`}
              onClick={(event) => {
                if (collapsed && section.children.length > 1) { setGroup({ anchor: event.currentTarget, section }); return; }
                navigate(section.path);
              }}
              aria-current={active ? 'page' : undefined}
              aria-haspopup={collapsed && section.children.length > 1 ? 'menu' : undefined}
              aria-label={section.label}
            >
              <Badge
                badgeContent={collapsed && section.badge ? section.badge : null}
                color="error"
                overlap="circular"
                invisible={!collapsed || !section.badge}
              >
                {section.icon}
              </Badge>
              {!collapsed && <span className={classes.itemLabel}>{section.label}</span>}
              {!collapsed && !!section.badge && <span className={classes.count} aria-label={`${section.badge} alerts`}>{section.badge > 99 ? '99+' : section.badge}</span>}
            </button>
          );
          if (collapsed) {
            return (
              <Tooltip key={section.key} title={section.label} placement="right">
                {content}
              </Tooltip>
            );
          }
          // The pages inside a group appear once you are in it, so the rail stays six lines long
          // until it has a reason to be longer.
          return (
            <React.Fragment key={section.key}>
              {content}
              {active && section.children.length > 1 && section.children.map((child) => (
                <button
                  key={child.key}
                  type="button"
                  className={`${classes.item} ${classes.child} ${matches(child.path) ? classes.childActive : ''}`}
                  onClick={() => navigate(child.path)}
                  aria-current={matches(child.path) ? 'page' : undefined}
                >
                  <span className={classes.itemLabel}>{child.label}</span>
                  {!!child.badge && <span className={classes.count} aria-label={`${child.badge} alerts`}>{child.badge > 99 ? '99+' : child.badge}</span>}
                </button>
              ))}
            </React.Fragment>
          );
        })}
      </nav>

      <div className={classes.footer}>
        <button type="button" className={classes.item} onClick={onNotifications} aria-label={unread ? `Notifications, ${unread} new since you last looked` : 'Notifications, nothing new'}>
          <Badge badgeContent={unread} color="error"><SummarizeIcon fontSize="small" /></Badge>
          {!collapsed && <span>Notifications</span>}
        </button>
        <button
          type="button"
          className={classes.account}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          aria-label="Account menu"
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
        >
          <Avatar className={classes.avatar}><PersonIcon fontSize="small" /></Avatar>
          {!collapsed && (
            <span style={{ minWidth: 0 }}>
              <Typography className={classes.accountName}>{user?.name || 'Account'}</Typography>
              <Typography className={classes.accountRole}>{ROLE_LABELS[user?.role] || 'Signed in'}</Typography>
            </span>
          )}
        </button>
        <Menu
          anchorEl={group?.anchor}
          open={Boolean(group)}
          onClose={() => setGroup(null)}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        >
          {(group?.section.children || []).map((child) => (
            <MenuItem
              key={child.key}
              selected={matches(child.path)}
              onClick={() => { navigate(child.path); setGroup(null); }}
            >
              {child.label}
            </MenuItem>
          ))}
        </Menu>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={() => { setAnchorEl(null); navigate('/account'); }}>
            My account
          </MenuItem>
          <Divider />
          {/* Appearance sits with the account rather than in settings: it is a property of the
              person at this screen, not of the tenant everyone shares. */}
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block' }}>Appearance</Typography>
          {[['system', 'Match my device', SettingsBrightnessIcon], ['light', 'Light', LightModeIcon], ['dark', 'Dark', DarkModeIcon]].map(([value, label, Icon]) => (
            <MenuItem key={value} selected={appearance === value} onClick={() => { setAppearance(value); setAnchorEl(null); }}>
              <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
              {label}
            </MenuItem>
          ))}
          <Divider />
          <MenuItem onClick={handleLogout}>Sign out</MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default NavRail;
